'use client';

import { useEffect, useRef } from 'react';
import { useAuth } from '@/lib/auth/provider';
import { useToast } from '@/components/shared/toast';

interface UseIdleTimeoutOptions {
  timeoutMinutes?: number;
  warningMinutes?: number;
}

const ACTIVITY_EVENTS: Array<keyof WindowEventMap> = [
  'mousemove',
  'keydown',
  'click',
];

/**
 * The last moment the person did anything in ANY tab of this browser
 * (2026-10-08 follow-up). The session it guards — `cl_auth_session` in
 * localStorage plus the `cl_auth_token` cookie — is shared by every tab, but
 * each tab used to time only its OWN activity: a background tab (which never
 * sees a mousemove) expired 30 minutes after it was last touched and signed
 * out the tab the person was working in. Deploys made it bite: the person
 * waits on a build with a second tab open, the stale tab's timer fires, and
 * the reload onto the new build lands on /login. Activity is now written
 * here and every tab checks it before signing anyone out.
 */
export const LAST_ACTIVITY_KEY = 'cl_last_activity';
/** Writes are throttled: mousemove fires constantly. */
const ACTIVITY_WRITE_EVERY_MS = 5_000;

/** A few seconds of clock disagreement between tabs is tolerated. */
const FUTURE_TOLERANCE_MS = 5_000;

/**
 * The shared timestamp, treated as UNTRUSTED (review I1): anything in the
 * same origin can write it, and a clock that stepped backwards leaves an
 * honest value "in the future". Only a finite, positive value that is not
 * later than now (plus a few seconds, clamped to now) counts; everything
 * else — NaN, negative, garbage, a future or absurd (`1e15`) value — reads
 * as "no shared activity", so this tab's own clock decides and a truly idle
 * browser still signs out within one window.
 */
export function readSharedActivity(now: number = Date.now()): number {
  let raw: string | null;
  try {
    raw = localStorage.getItem(LAST_ACTIVITY_KEY);
  } catch {
    return 0;
  }
  if (!raw || !/^\d{1,16}$/.test(raw)) return 0;
  const n = Number(raw);
  if (!Number.isFinite(n) || n <= 0) return 0;
  if (n > now + FUTURE_TOLERANCE_MS) return 0;
  return Math.min(n, now);
}

function writeSharedActivity(at: number): void {
  try {
    localStorage.setItem(LAST_ACTIVITY_KEY, String(at));
  } catch {
    // Storage blocked: this tab still times its own activity.
  }
}

function resolveTimeoutMinutes(opt: number | undefined): number {
  if (typeof opt === 'number' && opt > 0) return opt;
  const raw = process.env.NEXT_PUBLIC_IDLE_TIMEOUT_MINUTES;
  const parsed = raw ? Number.parseFloat(raw) : NaN;
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 30;
}

export function useIdleTimeout(opts?: UseIdleTimeoutOptions): void {
  const { isAuthenticated, clearLocalSession } = useAuth();
  const { toast, dismiss } = useToast();

  const warningTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const expiryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const warnedRef = useRef(false);
  /** The open "you'll be signed out" toast, so cross-tab activity can close it. */
  const warningToastRef = useRef<string | null>(null);
  /** Page arrival is counted once per mount, not on every route change. */
  const arrivalRecordedRef = useRef(false);

  const timeoutMinutes = resolveTimeoutMinutes(opts?.timeoutMinutes);
  const warningMinutes =
    typeof opts?.warningMinutes === 'number' && opts.warningMinutes > 0
      ? opts.warningMinutes
      : 2;

  useEffect(() => {
    if (!isAuthenticated) return;

    const timeoutMs = timeoutMinutes * 60_000;
    const warnAfterMs = Math.max(timeoutMs - warningMinutes * 60_000, 0);
    /** Every timer delay stays within [0, one window] — never past the
     *  2^31-1 setTimeout ceiling (which browsers turn into ~1 ms). */
    const clampDelay = (ms: number) => Math.min(timeoutMs, Math.max(0, ms));
    /** How long ago anyone used any tab, per the (validated) shared clock. */
    const sinceSharedActivity = () => {
      const now = Date.now();
      const shared = readSharedActivity(now);
      return shared > 0 ? now - shared : Number.POSITIVE_INFINITY;
    };
    const dismissWarning = () => {
      warnedRef.current = false;
      if (warningToastRef.current) {
        dismiss?.(warningToastRef.current);
        warningToastRef.current = null;
      }
    };

    const clearTimers = () => {
      if (warningTimerRef.current) {
        clearTimeout(warningTimerRef.current);
        warningTimerRef.current = null;
      }
      if (expiryTimerRef.current) {
        clearTimeout(expiryTimerRef.current);
        expiryTimerRef.current = null;
      }
    };

    let lastWrite = 0;
    const recordActivity = () => {
      const now = Date.now();
      if (now - lastWrite < ACTIVITY_WRITE_EVERY_MS) return;
      lastWrite = now;
      writeSharedActivity(now);
    };

    const expire = () => {
      // Another tab was used more recently: this one is not idle, the
      // person is. Wait out the rest of THEIR window instead.
      const sinceShared = sinceSharedActivity();
      if (sinceShared < timeoutMs) {
        scheduleTimers(timeoutMs - sinceShared);
        return;
      }
      clearTimers();
      dismissWarning();
      // Local only — NOT `logout()`, which revokes every session this person
      // has on every device (POST /api/auth/logout). Idling in one tab should
      // end this browser's session, nothing more. Explicit Sign out still
      // revokes server-side.
      try {
        clearLocalSession();
      } finally {
        if (typeof window !== 'undefined') {
          window.location.href = '/login?reason=idle';
        }
      }
    };

    const scheduleTimers = (requestedMs: number = timeoutMs) => {
      clearTimers();
      const remainingMs = clampDelay(requestedMs);
      const warnInMs = clampDelay(remainingMs - warningMinutes * 60_000);
      warningTimerRef.current = setTimeout(() => {
        // Same check as expiry: no warning while another tab is in use.
        const sinceShared = sinceSharedActivity();
        if (sinceShared < warnAfterMs) {
          scheduleTimers(timeoutMs - sinceShared);
          return;
        }
        warnedRef.current = true;
        warningToastRef.current = toast(
          'warning',
          `You'll be signed out in ${warningMinutes} minute${warningMinutes === 1 ? '' : 's'} due to inactivity. Click anywhere to stay signed in.`,
          { title: 'Session expiring', durationMs: 0 },
        );
      }, warnInMs);
      expiryTimerRef.current = setTimeout(expire, remainingMs);
    };

    const handleActivity = () => {
      // Activity here takes down this tab's own "session expiring" warning
      // too (review N1); dismissWarning also clears warnedRef.
      dismissWarning();
      recordActivity();
      scheduleTimers();
    };

    // Activity in another tab restarts this tab's clock too — and takes
    // down a warning this tab is already showing (review M2). The value is
    // re-validated: a bogus write restarts nothing.
    const handleStorage = (e: StorageEvent) => {
      if (e.key !== LAST_ACTIVITY_KEY) return;
      const sinceShared = sinceSharedActivity();
      if (sinceShared >= timeoutMs) return;
      dismissWarning();
      scheduleTimers(timeoutMs - sinceShared);
    };

    // Arriving on the page counts as activity ONCE per mount, and only in a
    // tab the person is looking at (review M2): a programmatic route change,
    // a polling redirect or a session-restored background tab is not the
    // person doing anything, and must not keep every tab signed in.
    if (
      !arrivalRecordedRef.current &&
      typeof document !== 'undefined' &&
      document.visibilityState === 'visible'
    ) {
      arrivalRecordedRef.current = true;
      recordActivity();
    }
    scheduleTimers();

    for (const evt of ACTIVITY_EVENTS) {
      window.addEventListener(evt, handleActivity, { passive: true });
    }
    window.addEventListener('storage', handleStorage);

    return () => {
      clearTimers();
      for (const evt of ACTIVITY_EVENTS) {
        window.removeEventListener(evt, handleActivity);
      }
      window.removeEventListener('storage', handleStorage);
    };
  }, [isAuthenticated, clearLocalSession, toast, dismiss, timeoutMinutes, warningMinutes]);
}
